import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  DataTable,
  Modal,
  FileUpload,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

const EMPTY_FORM = {
  titolo: "",
  descrizioneBreve: "",
  tipologiaInterventoId: "",
  attiva: true,
};

const EMPTY_STEP = {
  testo: "",
  existingFoto: null,
  newFile: null,
  removeFoto: false,
};

function GuideAutorisoluzionePage() {
  const { apiRequest, getAccessToken } = useAuth();
  const { showToast } = useToast();

  const [guide, setGuide] = useState([]);
  const [tipologieList, setTipologieList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
  const [fotoPreviews, setFotoPreviews] = useState({});
  const fotoPreviewsRef = useRef({});

  // Carica le foto esistenti come blob autenticati (il webUrl SharePoint non è
  // raggiungibile direttamente da <img> senza una sessione Microsoft attiva)
  useEffect(() => {
    const daCaricare = steps.filter(
      (s) => s.existingFoto && !s.removeFoto && !s.newFile,
    );
    if (!daCaricare.length) return;

    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      for (const s of daCaricare) {
        if (cancelled) break;
        const fotoId = s.existingFoto.id;
        if (fotoPreviewsRef.current[fotoId]) continue;
        try {
          const res = await fetch(`/api/guide-autorisoluzione/foto/${fotoId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok || cancelled) continue;
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          fotoPreviewsRef.current[fotoId] = url;
          if (!cancelled)
            setFotoPreviews((prev) => ({ ...prev, [fotoId]: url }));
        } catch {
          /* ignora errori preview singoli */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [steps, getAccessToken]);

  useEffect(() => {
    return () => {
      Object.values(fotoPreviewsRef.current).forEach(URL.revokeObjectURL);
    };
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [guideData, tipologieData] = await Promise.all([
        apiRequest("/guide-autorisoluzione"),
        apiRequest("/tipologie-intervento"),
      ]);
      setGuide(guideData.value || []);
      setTipologieList(tipologieData.value || []);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Errore nel caricamento dei dati", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setSteps([{ ...EMPTY_STEP }]);
    setModalOpen(true);
  };

  const handleOpenEdit = async (item) => {
    try {
      const detail = await apiRequest(`/guide-autorisoluzione/${item.id}`);
      setEditingItem(detail);
      setFormData({
        titolo: detail.titolo || "",
        descrizioneBreve: detail.descrizioneBreve || "",
        tipologiaInterventoId: detail.tipologiaInterventoId || "",
        attiva: detail.attiva !== false,
      });
      setSteps(
        (detail.steps || []).length > 0
          ? detail.steps.map((s) => ({
              testo: s.testo || "",
              existingFoto: s.foto || null,
              newFile: null,
              removeFoto: false,
            }))
          : [{ ...EMPTY_STEP }],
      );
      setModalOpen(true);
    } catch (error) {
      console.error("Error loading guida:", error);
      showToast("Errore nel caricamento della guida", "error");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addStep = () => setSteps((prev) => [...prev, { ...EMPTY_STEP }]);

  const removeStep = (idx) =>
    setSteps((prev) => prev.filter((_, i) => i !== idx));

  const moveStep = (idx, direction) => {
    setSteps((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const updateStepTesto = (idx, testo) =>
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, testo } : s)));

  const updateStepFile = (idx, file) =>
    setSteps((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, newFile: file, removeFoto: false } : s,
      ),
    );

  const removeStepFoto = (idx) =>
    setSteps((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, newFile: null, removeFoto: true } : s,
      ),
    );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.titolo.trim()) {
      showToast("Il titolo è obbligatorio", "error");
      return;
    }
    if (!formData.tipologiaInterventoId) {
      showToast("Seleziona la tipologia intervento", "error");
      return;
    }

    const validSteps = steps.filter((s) => s.testo.trim());
    if (validSteps.length === 0) {
      showToast("Aggiungi almeno uno step con testo", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("titolo", formData.titolo.trim());
      if (formData.descrizioneBreve.trim()) {
        payload.append("descrizioneBreve", formData.descrizioneBreve.trim());
      }
      payload.append("tipologiaInterventoId", formData.tipologiaInterventoId);
      payload.append("attiva", formData.attiva ? "true" : "false");

      const files = [];
      const stepsPayload = validSteps.map((s, idx) => {
        const ordine = idx + 1;
        if (s.newFile) {
          const newFotoIndex = files.length;
          files.push(s.newFile);
          return { ordine, testo: s.testo.trim(), newFotoIndex };
        }
        if (s.existingFoto && !s.removeFoto) {
          return { ordine, testo: s.testo.trim(), foto: s.existingFoto };
        }
        return { ordine, testo: s.testo.trim(), foto: null };
      });

      payload.append("stepsJson", JSON.stringify(stepsPayload));
      files.forEach((f) => payload.append("stepFoto", f));

      await apiRequest(
        editingItem
          ? `/guide-autorisoluzione/${editingItem.id}`
          : "/guide-autorisoluzione",
        {
          method: editingItem ? "PUT" : "POST",
          body: payload,
          headers: {},
        },
      );

      showToast(
        editingItem
          ? "Guida aggiornata con successo"
          : "Guida creata con successo",
        "success",
      );
      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving guida:", error);
      showToast(error.message || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/guide-autorisoluzione/${itemToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Guida eliminata con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting guida:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const tipologieOptions = tipologieList.map((t) => ({
    value: t.id,
    label: t.nome,
  }));

  const columns = [
    {
      key: "titolo",
      label: "Titolo",
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.titolo}</div>
          {row.descrizioneBreve && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.descrizioneBreve}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "tipologiaIntervento",
      label: "Tipologia Intervento",
      sortable: true,
      render: (row) => row.tipologiaIntervento || "-",
    },
    {
      key: "nStep",
      label: "Step",
      render: (row) => row.nStep,
    },
    {
      key: "attiva",
      label: "Stato",
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.attiva
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {row.attiva ? "Attiva" : "Bozza"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Azioni",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(row);
            }}
            className="p-1 text-primary-600 hover:text-primary-800"
            title="Modifica"
          >
            ✏️
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              confirmDelete(row);
            }}
            className="p-1 text-red-600 hover:text-red-800"
            title="Elimina"
          >
            🗑️
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">
              Admin
            </a>
            <span>/</span>
            <span>Guide Autorisoluzione</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Guide Autorisoluzione
          </h1>
          <p className="text-gray-500 mt-1">
            Guide passo-passo per aiutare gli utenti a risolvere autonomamente
            problemi di piccola entità, mostrate nel form di richiesta
            intervento in base alla tipologia selezionata
          </p>
        </div>

        <Button variant="primary" onClick={handleOpenCreate}>
          + Nuova Guida
        </Button>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={guide}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca guida..."
          emptyMessage="Nessuna guida di autorisoluzione trovata"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Modifica Guida" : "Nuova Guida"}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Titolo *"
            name="titolo"
            value={formData.titolo}
            onChange={handleChange}
            placeholder="es. Lavastoviglie: scarico intasato"
            maxLength={200}
          />

          <Textarea
            label="Descrizione breve"
            name="descrizioneBreve"
            value={formData.descrizioneBreve}
            onChange={handleChange}
            placeholder="Sottotitolo mostrato nel box, es. Prova questi passaggi prima di aprire una richiesta"
            rows={2}
            maxLength={300}
          />

          <Select
            label="Tipologia Intervento *"
            name="tipologiaInterventoId"
            value={formData.tipologiaInterventoId}
            onChange={handleChange}
            options={tipologieOptions}
            placeholder="Seleziona tipologia..."
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="attiva"
              checked={formData.attiva}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <div>
              <span className="text-gray-700">Guida attiva</span>
              <p className="text-xs text-gray-500">
                Se disattivata resta in bozza e non viene mostrata agli utenti
              </p>
            </div>
          </label>

          {/* Step */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">
                Step della guida
              </h3>
              <Button type="button" variant="ghost" onClick={addStep}>
                + Aggiungi step
              </Button>
            </div>

            {steps.map((step, idx) => (
              <div
                key={idx}
                className="p-3 bg-white border border-gray-200 rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    Step {idx + 1}
                  </span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveStep(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                      title="Sposta su"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(idx, 1)}
                      disabled={idx === steps.length - 1}
                      className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                      title="Sposta giù"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      disabled={steps.length === 1}
                      className="p-1 text-red-600 hover:text-red-800 disabled:opacity-30"
                      title="Rimuovi step"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <Textarea
                  value={step.testo}
                  onChange={(e) => updateStepTesto(idx, e.target.value)}
                  placeholder="Descrivi cosa fare in questo passo..."
                  rows={2}
                />

                {step.existingFoto && !step.removeFoto && !step.newFile ? (
                  <div className="flex items-center gap-3">
                    {fotoPreviews[step.existingFoto.id] ? (
                      <img
                        src={fotoPreviews[step.existingFoto.id]}
                        alt={step.existingFoto.nome || "Foto step"}
                        className="h-16 w-16 object-cover rounded border border-gray-200"
                      />
                    ) : (
                      <div className="h-16 w-16 flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs text-gray-400">
                        ...
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeStepFoto(idx)}
                    >
                      Rimuovi foto
                    </Button>
                  </div>
                ) : (
                  <FileUpload
                    accept="image/*"
                    maxSize={20}
                    multiple={false}
                    value={step.newFile}
                    onChange={(file) => updateStepFile(idx, file)}
                    helper="Foto opzionale per questo step"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setModalOpen(false)}
            >
              Annulla
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Salvataggio..." : editingItem ? "Aggiorna" : "Crea"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Guida"
        message={`Sei sicuro di voler eliminare la guida "${itemToDelete?.titolo}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default GuideAutorisoluzionePage;
